import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { type CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../../store/user/user'
import { useGetConnectorEntitiesQuery } from '../../../../../store/integrations/integrations'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { type SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { merge } from '../../../utils'
import { removeEmptyItems } from '../../../../../common/util'
import { useSaveIntegrationServiceConfig } from '../../../useSaveIntegrationServiceConfig'
import {
  type AdvisorEngineContactCreateFormValues,
  type AdvisorEngineContactCreateSettings,
  type AdvisorEngineInboundContactSyncSettingsFilters,
  type AdvisorEngineOfficeEntity,
  type AdvisorEngineSecurityGroupEntity,
  type AdvisorEngineSettingsFormValues,
} from './models'

const ADVISOR_ENGINE_CONTACT_SYNC_MAX_OBJECT_COUNT = 500

export const useAdvisorEngineContactSync = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const [autoSync, setAutoSync] = useState<boolean | null>(false)
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'advisorengine',
    direction: 'inbound',
    object: 'contact_operation_hashed',
    scheduling: '*/30 * * * *',
    payload: {
      maxObjectCount: ADVISOR_ENGINE_CONTACT_SYNC_MAX_OBJECT_COUNT,
    },
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [serviceConfig, setServiceConfig] = useState<{ [key: string]: any } | null>(null)
  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(true)

  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, 'advisorengine')
      setIsSyncDisabled(serviceIntegration?.isSyncDisabled ?? false)
    }
  }, [account])

  useEffect(() => {
    setAutoSync(checkpoint?.data?.active ?? null)
  }, [checkpoint])

  useEffect(() => {
    if (userData) {
      const integration = userData.integrations?.find((i) => i.serviceId === IntegrationServiceId.ADVISOR_ENGINE)
      if (integration) {
        setServiceConfig(integration.serviceConfig ?? null)
      }
    }
  }, [userData])

  const save = async (data: AdvisorEngineSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (autoSync == null) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest).unwrap()
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync }).unwrap()
      }
      const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ADVISOR_ENGINE)
      if (integration) {
        const filters: AdvisorEngineInboundContactSyncSettingsFilters = {
          active: data.inbound.contact.filters.active ?? undefined,
          contactType: data.inbound.contact.filters.contactType || undefined,
          tags: (data.inbound.contact.filters.tags ?? []).length > 0 ? data.inbound.contact.filters.tags : undefined,
        }
        const mergedServiceConfig = merge(integration.serviceConfig || {}, { inbound: { contact: { filters } } })
        await updateUserIntegration({
          accountId,
          integrationName: integration.serviceId,
          integrationId: integration.id,
          payload: { serviceConfig: removeEmptyItems(mergedServiceConfig) },
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const saveAutoSync = async (active: boolean) => {
    try {
      if (autoSync == null) {
        if (active) await createCheckpoint(checkpointRequest).unwrap()
      } else {
        await updateCheckpoint({ ...checkpointRequest, active }).unwrap()
      }
    } catch (e) {
      console.error(e)
    }
  }

  return {
    autoSync,
    serviceConfig,
    isSyncDisabled,
    isAccountFetching,
    isFetching: !isUpdating && (isGetMeQueryFetching || isGetCheckpointFetching),
    isSaving: isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading,
    save,
    saveAutoSync,
  }
}

const getFirstConnectorEntityValues = <TEntity>(data: unknown): TEntity[] => {
  if (!Array.isArray(data)) {
    return []
  }
  return (data as Array<Record<string, TEntity>>).map((entry) => Object.values(entry)[0]).filter((entity): entity is TEntity => entity !== undefined)
}

export const useAdvisorEngineContactCreate = ({ skipConfiguredEntities = false }: { skipConfiguredEntities?: boolean } = {}) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const { saveServiceConfig, isSaving } = useSaveIntegrationServiceConfig(IntegrationServiceId.ADVISOR_ENGINE)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ADVISOR_ENGINE)
  const savedContactCreate = integration?.serviceConfig?.contactCreate as AdvisorEngineContactCreateSettings | undefined
  const skipOffices = !userData || (skipConfiguredEntities && !!savedContactCreate?.officeId)
  const skipSecurityGroups = !userData || (skipConfiguredEntities && !!savedContactCreate?.securityGroupId)

  const { data: officesEntity, isFetching: isOfficesFetching } = useGetConnectorEntitiesQuery(
    {
      accountId,
      connector: IntegrationServiceId.ADVISOR_ENGINE,
      entityType: 'office',
      relatedTo: null,
    },
    { skip: skipOffices },
  )

  const { data: securityGroupsEntity, isFetching: isSecurityGroupsFetching } = useGetConnectorEntitiesQuery(
    {
      accountId,
      connector: IntegrationServiceId.ADVISOR_ENGINE,
      entityType: 'security_group',
      relatedTo: null,
    },
    { skip: skipSecurityGroups },
  )

  const officeOptions = useMemo<SelectOption[] | null>(() => {
    if (!officesEntity) {
      return null
    }
    return getFirstConnectorEntityValues<AdvisorEngineOfficeEntity>(officesEntity.data)
      .flatMap((entity) => entity.offices ?? [])
      .map((office) => ({ id: office.id, label: office.name }))
  }, [officesEntity])

  const securityGroupOptions = useMemo<SelectOption[] | null>(() => {
    if (!securityGroupsEntity) {
      return null
    }
    return getFirstConnectorEntityValues<AdvisorEngineSecurityGroupEntity>(securityGroupsEntity.data)
      .flatMap((entity) => entity.securityGroups ?? [])
      .map((securityGroup) => ({ id: securityGroup.securityGroupId, label: securityGroup.name }))
  }, [securityGroupsEntity])

  const save = async (data: AdvisorEngineContactCreateFormValues) => {
    try {
      setIsUpdating(true)
      await saveServiceConfig({
        contactCreate: {
          officeId: data.contactCreate.officeId || undefined,
          securityGroupId: data.contactCreate.securityGroupId || undefined,
        },
      })
    } finally {
      setIsUpdating(false)
    }
  }

  return {
    savedContactCreate,
    officeOptions,
    securityGroupOptions,
    isFetching: !isUpdating && (isGetMeQueryFetching || isOfficesFetching || isSecurityGroupsFetching),
    isSaving,
    save,
  }
}
